import { localeFormatDefault } from '@/plugins/date-print';
import { rServices, services } from '@/plugins/api';
import appConfig from '@/config';
import { base64ToBlob } from '@/utils/base64-to-blob';
import { getUniqueValuesArray } from '@/helpers/array';
import { EVENT_USER_STATUS, EVENT_CHATS_TYPES, DEFAULT_NOMINATION, EVENT_SPECIAL_PARAMS_FOR_UPDATE } from '@/modules/Events/constants.js';
import { TEAM_TYPES } from '@/modules/Team/constants';
import { PER_PAGE } from '@/constants/pagination';

import BaseModel from './base';
import EventOptions from './event-options';
import Nomination from './nomination';
import Stage from './stage';
import Chats from '@/models/list/chats.js';
import Participant from '@/models/list/participant';
import Team from '@/models/list/team';
import EventCertificate from '@/models/list/event-certificate';
import User from '@/utils/user';
import EventAttribute from './event-attribute';
import EventDocument from './event-document';
import NominationOptions from './nomination-options';
import EventForm from './event-form';
import EventPage from './event-page';
import { apiService } from '@/services/APIService';
import { rHandler } from '@/plugins/RHandler';
import { DEFAULT_PARAMS } from '@/utils/objectsRHandler';

export default class Event extends BaseModel {
  #user;

  constructor(event, url) {
    super(event, url);

    this.short_name = event.short_name;
    this.members = event.members || [];
    this.teams = [];
    this.active_members_count = event.active_members_count;
    this.members_count = event.members_count;
    this.request_members_count = event.request_members_count;
    this.reject_members_count = event.reject_members_count;
    this.restricted_members_count =  event.restricted_members_count;
    this.status = event.status;
    this.end_at = event.end_at;
    this.start_at = event.start_at;
    this.announcement = event.announcement;
    this.target = event.target;
    this.category_id = event.category_id;
    this.stages_count = event.stages_count;
    this.images = [];
    this.registerLink = `${window.location.origin}/event/register/${event.id}`;
    this.options = event.options ? new EventOptions(event.options, 'event-options') : null;
    this.options_id = event.options_id;
    this.area = event.area || null;
    this.area_id = event.area_id;
    this.owner_id = event.owner_id;
    this.organization = event.organization || {};
    this.organization_name = event.organization_name;
    this.main_image = event.main_image;
    this.experts = event.experts || [];
    this.highest_event = event.highest_event;
    this.nominations = event.nominations ? (event.nominations || []).map((nomination) => new Nomination(nomination)) : [];
    this.nomination_options = event.nomination_options ? (event.nomination_options || []).map((item) => new NominationOptions(item)) : [];
    this.stages = event.stages ? (event.stages || []).map((stage) => new Stage(stage)) : [];
    this.templates = event.templates || [];
    this.presentation_url = event.presentation_url;
    this.target = event.target;
    this.request_status = event.request_status || null;

    this.available_polls_count = event.available_polls_count || null;
    this.places_count = event.places_count || null;
    this.unread_notifications_count = event.unread_notifications_count || 0;
    this.news_count = event.news_count || 0;
    this.event_user_requests = event.event_user_requests || []; // { nomination_id, status }[]

    this.event_stage_start_at = localeFormatDefault(event.event_stage_start_at);
    this.event_stage_end_at = localeFormatDefault(event.event_stage_end_at);

    // organizers
    this.step = null;

    this.level = {
      id: event.level,
      name: appConfig.EVENT_LEVELS_TEXT[event.level],
    };
    this.document = null;

    this.is_favorite = false;

    // chats (telegram)

    this.chats = new Chats('event', event.id, event.telegram || []);

    this.results = null;
    this.eventCertificates = [];

    this.qr_link = event.qr_link || '';

    // organizers
    this.organizers = [];

    this.is_user_event_member = false;

    this.subordinated = event.subordinated || false;

    this.parent_event_id = event.parent_event_id || null;

    this.#user = new User();
  }

  update(rParams = DEFAULT_PARAMS) {
    const { data, obs } = rParams;
    const hasSpecial = Object.keys(data).some((i) => EVENT_SPECIAL_PARAMS_FOR_UPDATE.includes(i));
    return rHandler(
      apiService.put(`${this.url}/${this.id}${hasSpecial ? '/edit' : ''}`, data),
      (response) => {
        Object.assign(this, data);
        return { response };
      },
      (error) => ({ error }),
      obs,
    );
  }

  get shortName() {
    return this.short_name || '';
  }

  get isClosed() {
    return [appConfig.EVENT_STATUS.CLOSED, appConfig.EVENT_STATUS.MODERATE_REFUSE].includes(this.status);
  }

  get isDraft() {
    return this.status === appConfig.EVENT_STATUS.DRAFT;
  }

  get isModerate() {
    return this.status === appConfig.EVENT_STATUS.MODERATE;
  }

  get isActive() {
    return this.status === appConfig.EVENT_STATUS.ACTIVE;
  }

  get isAdminTemplate() {
    return this.status === appConfig.EVENT_STATUS_CONCEALED.TEMPLATE;
  }

  get isSingle() {
    // Индивидуальное участие
    return this.options?.event_member_type === appConfig.EVENT_MEMBER_TYPE.SINGLE;
  }

  get isTeamEvent() {
    // Командное участие
    return this.options?.event_member_type === appConfig.EVENT_MEMBER_TYPE.TEAM;
  }

  get isTeamMixed() {
    // Смешанное участие
    return this.options?.event_member_type === appConfig.EVENT_MEMBER_TYPE.SINGLE_TEAM;
  }

  get isOlympics() {
    return this.options?.is_olympics;
  }

  get isLowest() {
    // Самое нижестоящее мероприятие
    return !this.options?.can_subordinate;
  }

  get isMyOrganization() {
    // Является ли организация этого мероприятия моей?
    return this.organization?.id === this.#user?.profile?.id;
  }

  get isMyHighest() {
    const { id, highest_event } = this;
    return highest_event?.id === id;
  }

  get isHighest() {
    const { isMyHighest, options } = this;
    return isMyHighest && options?.can_subordinate;
  }

  get subordinatedLevels() {
    const { templates, level } = this;
    const subordinatedLevels = Object.values(appConfig.EVENT_LEVELS).filter((levelType) => levelType > level.id);
    return templates.filter(({ level: templateLevel }) => subordinatedLevels.includes(templateLevel));
  }

  get defaultNomination() {
    const defaultNomination = this.nominations.find((nomination) => nomination.name === DEFAULT_NOMINATION.NAME);
    return defaultNomination || null;
  }

  get nominationsWithoutDefault() {
    const { defaultNomination } = this;
    return this.nominations.filter((nomination) => nomination.id !== defaultNomination.id);
  }

  get actualNominations() {
    const { defaultNomination, nominationsWithoutDefault } = this;
    if (!defaultNomination) {
      return this.nominations || [];
    }

    // БАГ - has_nominations = false, хотя номинации есть
    if (this.options?.has_nominations) {
      return nominationsWithoutDefault;
    }

    return [defaultNomination];
  }

  get memberNominations() {
    const { nominations, event_user_requests } = this;
    return nominations.filter((nomination) => !!event_user_requests.find((request) => request.nomination_id === nomination.id));
  }

  get isWithoutStages() {
    return this.options?.is_without_stages;
  }

  get isWithFastRegister() {
    return this.options?.is_without_moderation;
  }

  get showQR() {
    const { qr_link } = this;
    return qr_link && this.isWithFastRegister;
  }

  get isLocal() {
    return this.level.id === appConfig.EVENT_LEVELS.local;
  }

  get isUserPassingByAge() {
    const { options } = this;
    return this.#user.profile.age >= options.min_age && this.#user.profile.age <= options.max_age;
  }

  get applicationsProgress() {
    const { active_members_count, options } = this;
    const maxProgressProcends = 100;
    const maxEventParticipantsCount = options.max_participants || active_members_count || 1;
    return Math.ceil((maxProgressProcends * active_members_count) / maxEventParticipantsCount);
  }

  async fetchImages() {
    const params = {
      'search[event_id]': this.id,
      sort: 'id',
    };
    const { response, error } = await rServices.events.getImages({ params });
    if (!error) this.images = response.data.data;
  }

  async fetchNominations(data) {
    const params = {
      ...data,
      'search[event_id]': this.id,
      'per-page': PER_PAGE.infinity,
    };

    const { response, error } = await rServices.events.getEventNominations({ params });
    if (error) {
      return error;
    }

    const nominations = response.data.data.map((nomination) => new Nomination(nomination));
    this.nominations = nominations;

    return response;
  }

  async fetchOptions(params = null) {
    const args = [this.options.id];

    const { response, error } = await rServices.events.showEventOptions({ args, params });
    if (error) {
      return;
    }

    this.options = new EventOptions(response.data.data, 'event-options');
  }

  async updateOptions(params) {
    const id = this.options?.id || this.options_id || null;
    if (!id) return null;
    const response = await rServices.events.updateEventOptions({ args: [id, params] });
    if (!response.error) {
      const { data } = response.response.data;
      this.options = new EventOptions(data, 'event-options');
    }
    return response;
  }

  async updateEventArea(params) {
    const id = this.area?.id || this.area_id || null;
    if (!id) return null;
    const data = {
      ...params,
      event_id: this.id,
      id,
    };
    const { response } = await rServices.events.updateEventArea({ args: [id], data });
    this.area = response?.data?.data || null;
    return response;
  }

  async openDocument() {
    if (!this.document) {
      const response = await services.events.getPdf(this.id, { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      this.document = base64ToBlob(response.data);
    }
    window.open(URL.createObjectURL(this.document), '_blank');
  }

  close() {
    return rServices.events.closeEvent({ args: [this.id] });
  }

  async publish() {
    const { response, error } = await rServices.events.publishEvent({ args: [this.id] });
    if (!error) {
      this.status = this.isLocal ? appConfig.EVENT_STATUS.ACTIVE : appConfig.EVENT_STATUS.MODERATE;
    }
    return { response, error };
  }

  clone() {
    return rServices.events.cloneEvent({ args: [this.id] });
  }

  async deleteImage(id) {
    const response = await rServices.events.deleteImage({ args: [id] });
    this.images = this.images.filter((i) => i.id !== id);
    await this.#checkMainImage();
    return response;
  }

  async addImage(imageName, isMain) {
    const params = {
      event_id: this.id,
      name: imageName,
      is_main: isMain,
    };
    const { response, error } = await rServices.events.createImage({ params });
    if (!error) this.images.push(response.data.data);
    await this.#checkMainImage();
    return response;
  }

  async makeMainImage(id) {
    const params = {
      is_main: true,
    };
    const { error } = await rServices.events.updateImage({ args: [id], params });
    if (!error) {
      const mainImage = this.images.find((i) => i.id === id);
      this.main_image = mainImage;
      this.images = [{ ...mainImage, is_main: true }, ...this.images.filter((i) => i.id !== id).map((i) => ({ ...i, is_main: false }))];
    }
  }

  async #checkMainImage() {
    const existsMainImage = this.images.find((image) => image.is_main);
    const [firstImage] = this.images;
    if (!this.main_image && existsMainImage?.id) {
      this.main_image = existsMainImage;
      return;
    }
    if (!existsMainImage && firstImage) {
      await this.makeMainImage(firstImage.id);
      return;
    }
    if (existsMainImage?.id !== this.main_image?.id) this.main_image = null;
  }

  // organizers steps
  async fetchStep() {
    const { response, error } = await rServices.events.getOrganizersStep({ params: { 'search[event_id]': this.id } });
    if (!error) [this.step] = response.data.data;
  }

  async setStep(step) {
    const { response, error } = await rServices.events.addOrganizersStep({ args: [this.step.id], data: { step } });
    if (!error) this.step = response.data.data;
  }

  nextStep() {
    return this.setStep(this.step.step + 1);
  }

  backStep() {
    return this.setStep(this.step.step - 1);
  }

  get isOwner() {
    return this.#user?.isAuthenticated && this.owner_id === this.#user?.data?.id;
  }

  // EVENT CHATS (telegram)

  get membersChat() {
    return this.chats.list.find((chat) => chat.options.type === EVENT_CHATS_TYPES.members) || null;
  }

  get expertsChat() {
    return this.chats.list.find((chat) => chat.options.type === EVENT_CHATS_TYPES.experts) || null;
  }

  get organizatorsChat() {
    return this.chats.list.find((chat) => chat.options.type === EVENT_CHATS_TYPES.organizators) || null;
  }

  async userInfoCompleted() {
    // return rServices.events.checkUserInfoCompleted({ args: [this.id] });
    const { item, ...other } = await this.getFormMember(this.#user?.data?.id);
    const attributes = (item.attributes || []).filter(({ isSystem, user_value }) => !isSystem && !user_value);
    const documents = (item.documents || []).filter(({ user_value }) => !user_value);
    return {
      item: {
        documents,
        attributes,
        length: Number(attributes.length + documents.length),
        lengthRequired: [...attributes, ...documents].filter(({ required }) => required).length,
      },
      ...other,
    };
  }

  // Members

  async getMembers(additionalParams) {
    const { getMemberEvent, getPublicMemberEvent } = rServices.rewards;
    const method = this.isOwner ? getMemberEvent : getPublicMemberEvent;

    const params = {
      'search[member_status]': EVENT_USER_STATUS.STATUS_ACTIVE,
      // 'per-page': PER_PAGE.infinity, // Избавляемся от этого, оно плохо кончится
      ltp: 1,
      ...additionalParams,
    };
    const args = [this.id];

    const { response, error } = await method({ args, params });
    if (error) {
      return { error };
    }

    this.members = response.data.data.map((member) => new Participant(member));

    return { response };
  }

  get isActiveEventMember() {
    const { event_user_requests } = this;
    return (event_user_requests || []).some(({ status }) => status === EVENT_USER_STATUS.STATUS_ACTIVE);
  }

  get isUserEventMember() {
    return this.is_user_event_member;
  }

  async checkIsUserEventMember() {
    const params = {
      'search[id]': this.#user.data?.id || null,
    };
    const args = [this.id];

    const { response, error } = await rServices.events.getPublicEventMembers({ args, params });
    if (error || !response.data.data.length) {
      this.is_user_event_member = false;
      return;
    }

    this.is_user_event_member = true;
  }

  // Teams

  async getTeams(additionalParams) {
    const { getMemberEventTeams, getPublicMemberEventTeams } = rServices.rewards;
    const method = this.isOwner ? getMemberEventTeams : getPublicMemberEventTeams;

    const params = {
      expand: 'members',
      // 'per-page': PER_PAGE.infinity,
      ...additionalParams,
    };
    const args = [this.id];

    const { response, error } = await method({ args, params });
    if (error) {
      return { error };
    }

    this.teams = response.data.data.map((team) => new Team(team, TEAM_TYPES.stage));

    return { response };
  }

  get userTeam() {
    return this.teams.find((team) => team.members.some((member) => member.id === this.#user.data.id));
  }

  // Un/assigned Prize Places Count

  // get assignedPrizePlacesForMembersCount() {
  //   return this.members.reduce((acc, member) => {
  //     const { prize_places } = member;
  //     if (!prize_places) {
  //       return acc;
  //     }

  //     return acc + prize_places.length;
  //   }, 0);
  // }

  // get assignedPrizePlacesForTeamsCount() {
  //   return this.teams.reduce((acc, team) => {
  //     const { places } = team;
  //     if (!places) {
  //       return acc;
  //     }

  //     return acc + places.length;
  //   }, 0);
  // }

  // get hasUnassignedPrizePlaces() {
  //   const map = {
  //     [EVENT_MEMBER_TYPE.single]: this.assignedPrizePlacesForMembersCount,
  //     [EVENT_MEMBER_TYPE.team]: this.assignedPrizePlacesForTeamsCount,
  //   };

  //   const assignedPrizePlacesCount = map[this.options.event_member_type];

  // return assignedPrizePlacesCount < this.options.prize_place_count;
  // }

  async getUnassignedPrizePlaces() {
    const { getEventPlaces, getPublicEventPlaces } = rServices.rewards;
    const method = this.isOwner ? getEventPlaces : getPublicEventPlaces;

    const params = {
      'search[empty_place]': 1,
    };
    const args = [this.id];

    const { response } = await method({ args, params });
    return response?.data?.data || [];
  }

  // Results

  async getResults(additionalParams) {
    const userId = this.#user.data.id;

    const params = {
      ...additionalParams,
      'search[id]': this.id,
      expand: 'stages,options,places,nominations,scores',
    };
    const args = [userId];

    const { response, error } = await rServices.events.getEventsResults({ args, params });
    if (error) {
      return { error };
    }

    const getNominationName = (nominations, nominationId) => {
      const nomination = nominations.find(({ id }) => id === nominationId);

      return nomination.name;
    };

    const results = response.data.data.map((result) => {
      const { stages, events } = result.places;

      const stagePlaces = stages.map((stagePlace) => {
        const { name: stageName, score } = result.stages.find(({ id }) => id === stagePlace.id);
        const nominationName = getNominationName(result.nominations, stagePlace.nomination_id);

        return {
          ...stagePlace,
          nominationName,
          stageName,
          score,
        };
      });

      const eventPlaces = events.map((eventPlace) => {
        const eventName = result.short_name;
        const nominationName = getNominationName(result.nominations, eventPlace.nomination_id);

        return {
          ...eventPlace,
          nominationName,
          eventName,
        };
      });

      return {
        ...result,
        places: {
          stages: stagePlaces,
          events: eventPlaces,
        },
      };
    });

    this.results = results;
    response.data.data = results;

    return { response };
  }

  // Event Certificates

  async getEventCertificatesForMember() {
    const params = {
      'search[event_id]': this.id,
      'search[user_id]': this.#user.data.id,
    };

    const { response, error } = await rServices.events.getCertificates({ params });
    if (error) {
      return { error };
    }

    this.eventCertificates = response.data.data.map((eventCertificate) => new EventCertificate(eventCertificate));

    return { response };
  }

  getEventCertificateFile(nominationId) {
    const certificate = this.eventCertificates.find(({ nomination_id }) => nomination_id === nominationId);
    const eventName = this.short_name;

    return certificate.getFile(eventName);
  }

  // Organizers

  async getOrganizers() {
    if (!this.isHighest) {
      const temporaryOrganizers = [];

      const subEventsParams = {
        'search[root_event_id]': this.highest_event.id,
        expand: 'organization',
      };
      const { response: subEventsResponse, error: subEventsError } = await rServices.events.getPublicEvents({ params: subEventsParams });
      if (subEventsError) {
        return;
      }

      const subEventsOrganizers = subEventsResponse.data.data.map(({ organization }) => organization);
      temporaryOrganizers.push(...subEventsOrganizers);

      const rootEventsParams = {
        'search[id]': this.highest_event.id,
        expand: 'organization',
      };
      const { response: rootEventsResponse, error: rootEventsError } = await rServices.events.getPublicEvents({ params: rootEventsParams });
      if (rootEventsError) {
        return;
      }

      const rootEventsOrganizers = rootEventsResponse.data.data.map(({ organization }) => organization);
      temporaryOrganizers.push(...rootEventsOrganizers);

      this.organizers.push(...getUniqueValuesArray(temporaryOrganizers));
    } else {
      this.organizers.push(this.organization);
    }
  }

  getAttributes(params) { // Все аттрибуты со всех цепочек
    return this.#getRequirements({
      method: rServices.events.getEventGlobalAttributes,
      Model: EventAttribute,
      url: 'event-global-attributes',
      params,
    });
  }

  getDocuments(params) { // Все документы со всех цепочек
    return this.#getRequirements({
      method: rServices.events.getNewEventDirectoryDocuments,
      Model: EventDocument,
      url: 'event-directory-document',
      params,
    });
  }

  getForm(params) {
    return this.#getRequirements({
      method: rServices.events.getEventForm,
      Model: EventForm,
      params,
      multiple: false,
    });
  }

  getFormMember(memberId, params, cached = false) {
    const method = {
      [true]: 'getEventFormMemberCached',
      [false]: 'getEventFormMember',
    }[cached];
    return this.#getRequirements({
      method: rServices.events[method],
      args: [memberId],
      Model: EventForm,
      params,
      multiple: false,
    });
  }

  #getRequirements({ args, params, method, Model, url, multiple = true }) {
    return method({ args: [
      this.id,
      ...(args || []),
      { sort: 'id', 'per-page': PER_PAGE.infinity, ...params },
    ] }).then(({ response, error }) => ({
      response,
      error,
      ...multiple ? {
        items: (response?.data?.data || []).map((item) => new Model(item, url)),
      } : {
        item: new Model(response?.data?.data || {}, url),
      },
    }));
  }

  getPagesConstructor(params) {
    return rServices.eventPages.getPages({ args: [this.id], params }).then(({ response, error }) => ({
      response,
      error,
      items: (response?.data?.data || []).map((item) => new EventPage(item)),
    }));
  }

  savePagesConstructor(data) {
    return rServices.eventPages.savePages({ args: [this.id], data }).then(({ response, error }) => ({
      response,
      error,
      items: (response?.data?.data || []).map((item) => new EventPage(item)),
    }));
  }

  get isManyLevels() {
    return this.organizers.length > 1;
  }

  // TODO: масштабировать для регистрации на мероприятие
  get registerTitle() {
    const options = this.options || {};
    return 'Принять участие';
    // const options = this.options || {};
    // const isRegisterPeriod = options.isRegisterPeriod || this.isHighest;
    // switch (true) {
    //   case this.isClosed:
    //     return 'Мероприятие завершено';
    //   case options.isRegisterNotStart:
    //     return 'Запись еще не началась';
    //   case isRegisterPeriod:
    //     return 'Принять участие';
    //   case options.isRegisterEnd:
    //   default:
    //     return 'Запись завершена';
    // }
  }

  get hasNominations() {
    return this.options.has_nominations;
  }
}
